// Contracts — kids cabinet DASHBOARD (staff demographics) (2026-06-28).
// Port of the javelin "Обзор" overview onto kids data (no ACCEPTED/PENDING
// funnel — round-2 decision). Shapes follow the dashboard port plan
// (Obsidian: Kids/веб новый/Дашборд — план порта). Scope is ALWAYS narrowed by
// the caller's role on the backend; query params only sub-filter inside what the
// role already allows. Demographics are counted over SHOT sessions (like the
// javelin etalon counts over participated sessions), joined to children.
import { z } from 'zod';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// GET /api/kids/dashboard/stats?office=&moderator=&from=&to=
export const KidsDashboardReqSchema = z.object({
  // manager: filters office; admin: forced to own office (ignored if mismatched);
  // moderator: ignored (always self).
  office: z.string().trim().max(60).optional(),
  // users.id of a moderator to drill into (manager/admin only).
  moderator: z.string().trim().max(64).optional(),
  // YYYY-MM-DD window over sessions.started_at (epoch ms). Empty → all-time.
  from: z.string().regex(DATE_RE).optional(),
  to: z.string().regex(DATE_RE).optional(),
  // ADMIN scope switch (Tim 07.21): 'office' = own office (default, старое
  // поведение), 'all' = everything (like a manager, aggregate numbers only).
  // Ignored for manager/moderator.
  view: z.enum(['office', 'all']).optional(),
});
export type KidsDashboardReq = z.infer<typeof KidsDashboardReqSchema>;

export const KidsToneBucketSchema = z.object({
  tone: z.number().int().min(1).max(10),
  count: z.number().int().nonnegative(),
});
export type KidsToneBucket = z.infer<typeof KidsToneBucketSchema>;

export const KidsAgeBucketSchema = z.object({
  // Canon buckets (packer normalize.age_group / validate.py + client deck):
  // 5-10 / 11-14 / 15-17. Was 5-8/9-12/13-17 (wrong) until 2026-07-07.
  key: z.enum(['5-10', '11-14', '15-17']),
  count: z.number().int().nonnegative(),
  pct: z.number().int().min(0).max(100),
});
export type KidsAgeBucket = z.infer<typeof KidsAgeBucketSchema>;

export const KidsDashboardResSchema = z.object({
  // The effective scope the backend applied (frontend hides controls by it).
  scope: z.object({
    role: z.enum(['manager', 'admin', 'moderator']),
    office: z.string().nullable(),
    moderator: z.string().nullable(),
  }),
  counts: z.object({
    booked: z.number().int().nonnegative(), // meetings (status='scheduled')
    started: z.number().int().nonnegative(), // sessions in progress
    done: z.number().int().nonnegative(), // shot sessions
    // clips shipped to R2 (uploaded_at set) — the Снято→Отгружено→Упаковано
    // pipeline tile (Tim 05.07). Optional so pre-05.07 workers keep validating.
    uploaded: z.number().int().nonnegative().optional(),
    packed: z.number().int().nonnegative(), // sessions.packed=1
    canceled: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(), // = done (shot children)
  }),
  // client verdicts from the tracker sync (0017, Tim 04.07): «принято /
  // не принято / invoiced». Optional so pre-0017 workers keep validating.
  clientCounts: z
    .object({
      accepted: z.number().int(),
      rejected: z.number().int(),
      invoiced: z.number().int(),
    })
    .optional(),
  toneHistogram: z.array(KidsToneBucketSchema), // length 10 (tone 1..10), gaps = 0
  genderSplit: z.object({
    male: z.number().int().nonnegative(),
    female: z.number().int().nonnegative(),
    other: z.number().int().nonnegative(),
    pct_male: z.number().int().min(0).max(100),
    pct_female: z.number().int().min(0).max(100),
    pct_other: z.number().int().min(0).max(100),
  }),
  ageBuckets: z.array(KidsAgeBucketSchema), // length 3
  kpi: z.object({
    // 'goal' = progress toward the delivery target (done / goal target), the
    // primary manager/admin KPI (Tim 08.07). 'plan' = today's completion
    // (done_today / planned_today), fallback when no goal target. 'conversion'
    // (done/booked) is the last-resort fallback. packed_ratio reserved.
    kind: z.enum(['goal', 'plan', 'conversion', 'packed_ratio']),
    value: z.number(), // percent, one decimal
    // goal target the value is a fraction of (only for kind='goal') — lets the
    // card label read «к цели N». null for the other kinds.
    target: z.number().int().nonnegative().nullable().optional(),
  }),
  plan: z.object({
    today: z.number().int().nullable(), // mod_plans.planned_today (sum across scope)
    week: z.number().int().nullable(), // mod_plans.planned_week
    done_today: z.number().int().nonnegative(),
    done_week: z.number().int().nonnegative(),
  }),
  today: z.number().int().nonnegative(), // shot today
  week: z.number().int().nonnegative(), // shot this week
});
export type KidsDashboardRes = z.infer<typeof KidsDashboardResSchema>;

// ── GET /api/kids/dashboard/distribution (ALL staff, GLOBAL — no scope clamp) ─
// Project-wide reference distribution: the skin-tone histogram (1..10) + age
// buckets over EVERY shot child, so a moderator sees the same «эталон» the
// manager does (the /stats endpoint clamps a moderator to self, hiding the
// project picture — Tim confirmed a dedicated global endpoint 2026-07-07).
// This returns AGGREGATE percentages only — no per-child / per-moderator rows —
// so exposing it to every staff role leaks nothing individual. `total` is the
// number of shot children the histogram/buckets are computed over.
export const KidsDistributionResSchema = z.object({
  toneHistogram: z.array(KidsToneBucketSchema), // length 10 (tone 1..10), gaps = 0
  ageBuckets: z.array(KidsAgeBucketSchema), // length 3 (5-10 / 11-14 / 15-17)
  // Global gender fact for the dashboards' «План: пол» (Tim 05.08 — the
  // moderator dashboard compared personal counts against the cluster goal).
  // Aggregate counts only. Optional so pre-05.08 workers keep validating.
  genderSplit: z
    .object({
      male: z.number().int().nonnegative(),
      female: z.number().int().nonnegative(),
      other: z.number().int().nonnegative(),
      pct_male: z.number().int().min(0).max(100),
      pct_female: z.number().int().min(0).max(100),
      pct_other: z.number().int().min(0).max(100),
    })
    .optional(),
  total: z.number().int().nonnegative(),
});
export type KidsDistributionRes = z.infer<typeof KidsDistributionResSchema>;

// ── GET /api/kids/dashboard/offices (manager: all; admin: own office) ────────
// Oversight grid: one row per office with summary counts + the per-moderator
// breakdown that lives behind each row's detail modal. Scope is forced on the
// backend: manager → every office; admin → ONLY its own office (its cabinet's
// "Офис" tab consumes this); plain moderators/reception get 403. Counts mirror the
// /stats handler (sessions JOIN children JOIN meetings on m.office); `booked` is
// scheduled meetings in that office. A moderator is attributed to the office of
// the sessions they shot (a mod can shoot in several offices), name from users.
export const KidsOfficeModeratorSchema = z.object({
  id: z.string(), // users.id
  name: z.string(),
  // Moderator's shooting ID (users.pid, 2 digits) — shown as «#NN» in the manager
  // office view (Tim 09.07). Optional+default for back-compat with older payloads.
  pid: z.string().nullable().optional().default(null),
  done: z.number().int().nonnegative(), // shot sessions
  packed: z.number().int().nonnegative(), // sessions.packed=1
  started: z.number().int().nonnegative(),
  canceled: z.number().int().nonnegative(),
  today: z.number().int().nonnegative(), // shot today (office TZ)
  week: z.number().int().nonnegative(), // shot rolling 7d (office TZ)
});
export type KidsOfficeModerator = z.infer<typeof KidsOfficeModeratorSchema>;

export const KidsOfficeOverviewSchema = z.object({
  office: z.string(), // offices.office id
  name: z.string(), // display name (offices.name; falls back to the id server-side)
  cluster: z.string(), // 'atlanta' | 'remote' (offices.cluster)
  booked: z.number().int().nonnegative(),
  started: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  packed: z.number().int().nonnegative(),
  canceled: z.number().int().nonnegative(),
  today: z.number().int().nonnegative(),
  week: z.number().int().nonnegative(),
  moderators: z.array(KidsOfficeModeratorSchema),
});
export type KidsOfficeOverview = z.infer<typeof KidsOfficeOverviewSchema>;

export const KidsOfficesOverviewResSchema = z.object({
  offices: z.array(KidsOfficeOverviewSchema),
});
export type KidsOfficesOverviewRes = z.infer<typeof KidsOfficesOverviewResSchema>;

// ── GET /api/kids/dashboard/sessions — monitoring session list (Tim 05.07) ───
// «Мониторинг для менеджмента»: the dashboard stat tiles open a modal with the
// full session list behind that number — ordered NEW→OLD (s.id DESC, freshest
// first — Tim 05.07). One endpoint, one `filter` enum covering both the status
// tiles (started/done/uploaded/packed/canceled) and the client-verdict tiles
// (accepted/rejected/invoiced). Role scope mirrors /dashboard/stats exactly
// (manager=all, admin=own office, moderator=self); office/moderator/from/to
// sub-filter inside the allowed scope.
export const KIDS_MONITOR_FILTERS = [
  'all',
  'started',
  'done',
  'uploaded', // clips shipped to R2 (sessions.uploaded_at set)
  'packed',
  'canceled',
  'client_accepted',
  'client_rejected',
  'client_invoiced',
] as const;
export type KidsMonitorFilter = (typeof KIDS_MONITOR_FILTERS)[number];

export const KidsMonitorReqSchema = z.object({
  office: z.string().trim().max(60).optional(),
  moderator: z.string().trim().max(64).optional(),
  from: z.string().regex(DATE_RE).optional(),
  to: z.string().regex(DATE_RE).optional(),
  filter: z.enum(KIDS_MONITOR_FILTERS).optional(), // omitted → 'all'
  // page offset; page size is fixed server-side (the FE's «Показать ещё»)
  offset: z.coerce.number().int().min(0).max(100000).optional(),
  // NB: deliberately NO admin `view` here — session lists are per-office data,
  // an admin's «Общий» widens only the aggregate /stats (Tim 07.21).
});
export type KidsMonitorReq = z.infer<typeof KidsMonitorReqSchema>;

export const KidsMonitorSessionSchema = z.object({
  id: z.number().int(), // sessions.id — the queue order key
  // client PID P<modPid:2><seq:4>; null when the session's moderator has no pid
  pid: z.string().nullable(),
  childName: z.string().nullable(),
  moderatorName: z.string().nullable(),
  office: z.string().nullable(), // meeting's office, walk-in → moderator's own
  startedAt: z.number().int().nullable(), // epoch ms of the shoot
  status: z.string(), // started | done | canceled
  uploaded: z.boolean(),
  packed: z.boolean(),
  clientStatus: z.enum(['accepted', 'rejected', 'pending']).nullable(),
  clientInvoiced: z.boolean(),
});
export type KidsMonitorSession = z.infer<typeof KidsMonitorSessionSchema>;

export const KidsMonitorResSchema = z.object({
  total: z.number().int().nonnegative(), // rows matching the filter (all pages)
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive(), // server page size
  sessions: z.array(KidsMonitorSessionSchema),
});
export type KidsMonitorRes = z.infer<typeof KidsMonitorResSchema>;
